import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useFullscreen } from '../../layouts/StackedLayout/StackedLayout';
import StaticSummaryGroup, { type StaticSummaryGroupItem } from './shared/StaticSummaryGroup';
import { createRadialLabelTickRenderer, type SelfDeterminationStatistic } from './shared/utils';

interface SelfDeterminationResultProps {
    statistics: SelfDeterminationStatistic;
    summaryFormation?: number[];
}

const SelfDeterminationResult = ({ statistics, summaryFormation = [2, 2] }: SelfDeterminationResultProps) => {
    const { t } = useTranslation('assessmentResultManagementPage');
    const { isMobile } = useFullscreen();

    const summaryItems: StaticSummaryGroupItem[] = useMemo(
        () =>
            statistics.summaryCards.map(item => ({
                title: `${item.value}/${item.total}`,
                description: item.label,
            })),
        [statistics.summaryCards]
    );

    return (
        <div>
            <div className="mx-auto grid max-w-3xl gap-5">
                {/* Statistics */}
                <div className="w-full">
                    <StaticSummaryGroup formation={summaryFormation} isMobile={isMobile} items={summaryItems} />
                </div>

                {/* Results */}
                <div className="flex w-full flex-col items-center justify-center">
                    <div className={`w-full ${isMobile ? 'aspect-3/2 max-h-[38vh]' : 'aspect-16/10 max-h-[50vh]'}`}>
                        <ResponsiveContainer>
                            <RadarChart
                                outerRadius="75%"
                                data={statistics.radarData}
                                margin={{
                                    top: 8,
                                    right: isMobile ? 44 : 64,
                                    bottom: isMobile ? 24 : 32,
                                    left: isMobile ? 44 : 64,
                                }}
                            >
                                <PolarGrid strokeDasharray="3 3" stroke="#b7b8b9" />
                                <PolarAngleAxis
                                    dataKey="shortLabel"
                                    tick={createRadialLabelTickRenderer(isMobile ? 12 : 16, {
                                        fontSize: 12,
                                        fill: 'var(--color-neutral-500)',
                                        fontWeight: 500,
                                    })}
                                    stroke="#2221212c"
                                />
                                <PolarRadiusAxis
                                    angle={90}
                                    domain={[0, statistics.domainMax]}
                                    tick={{
                                        fontSize: 11,
                                        fill: 'var(--color-neutral-500)',
                                        dy: 0,
                                    }}
                                    axisLine={{ stroke: '#dce1ebff' }}
                                    tickLine={{ stroke: '#f13bbbff' }}
                                    stroke="#bff00eff"
                                    tickCount={Math.min(11, statistics.domainMax + 1)}
                                    tickFormatter={value => value.toString()}
                                    orientation="middle"
                                />
                                <Radar
                                    name="Your Score"
                                    dataKey="value"
                                    stroke="var(--color-teal-600)"
                                    fill="#1fac7d56"
                                    fillOpacity={0.2}
                                    strokeWidth={2.5}
                                    dot={true}
                                    activeDot={{ r: 5 }}
                                />
                                <Tooltip
                                    formatter={(value: string) => [value, t('details.selfDetermination.yourScore')]}
                                    labelFormatter={label => `${t('details.selfDetermination.category')}: ${label}`}
                                    contentStyle={{
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        borderColor: '#c7c5c5ff',
                                        boxShadow: 'var(--shadow)',
                                        color: 'hsl(var(--foreground))',
                                        padding: '0.75rem 1rem',
                                    }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SelfDeterminationResult;
